import type { CSSProperties } from 'react';
import type { Toast, ToastOptions } from 'react-hot-toast';

import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

import useClassy from '@core/hooks/useClassy';
import classy from '@core/utils/classy';

import Box from '@ui/Box';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';

import classes from './style.module.scss';

// Default duration for toast notifications
const DEFAULT_DURATION = 8000;

/**
 * In order for notify's toast() to work, <NotificationToaster /> must exist in the DOM
 * Both packages/react/src/Hub and packages/react/src/Dash base index files should already have this component
 * */
export const notify = (
  notificationToBeToasted: Toast['message'],
  /** Note: some ToastOptions will not be applied because we are using toast.custom() */
  options: ToastOptions = {},
) => {
  toast.custom(
    t => {
      // We need to tap into the toast state to properly have enter/exit animations
      const notification =
        typeof notificationToBeToasted === 'function' ? notificationToBeToasted(t) : notificationToBeToasted;

      // Pass in the toast id to the Notification component so that it can take actions on it
      interface HasToastId {
        toastId: string;
      }
      const notificationWithToastId = React.isValidElement(notification)
        ? React.cloneElement(notification as React.ReactElement<HasToastId>, { toastId: t.id })
        : notification;

      return (
        <div className={classy(t.visible ? `${classes.Notification_open}` : `${classes.Notification_close}`)}>
          {notificationWithToastId}
        </div>
      );
    },
    {
      position: 'bottom-right',
      duration: DEFAULT_DURATION,
      ...options,
    },
  );
};

export interface NotificationProps {
  /** Optional ariaProps - defaults to role='status' with aria-live='polite' */
  ariaProps?: {
    'aria-live': 'assertive' | 'off' | 'polite';
    role: 'alert' | 'status';
  };
  /** Content rendered in the notification; doesn’t work with title or description */
  children?: React.ReactNode;
  className?: string;
  /** Optional description (will only be used if children prop is not passed) */
  description?: React.ReactNode;
  /** Whether or not notification can be dismissed via close icon */
  dismissible?: boolean;
  /** Optional emoji to show to the left of notification content */
  emoji?: string;
  /** Optional onClick callback */
  handleClick?: () => void;
  /** Optional override for icon */
  iconName?: string; // todo: this should map to valid <Icon> enum/types
  /** Type of notification (determines default icon and color) */
  kind?: 'destructive' | 'error' | 'secondary' | 'success';
  /** Optional set of inline styles. Usually `className` is preferred, but this
   * is useful for setting styles that need to be computed by JS. */
  style?: CSSProperties;
  theme?: 'dark' | 'light';
  /** Optional title (will only be used if children prop is not passed) */
  title?: string;
  /** Passed in when called using notify(), can be used to take actions on a specific toast instance (e.g. dismissing) */
  toastId?: string;
}

const Notification = ({
  ariaProps = {
    role: 'status',
    'aria-live': 'polite',
  },
  children,
  className,
  description,
  dismissible = false,
  handleClick,
  iconName,
  kind,
  style,
  theme = 'light',
  title,
  toastId,
}: NotificationProps) => {
  const bem = useClassy(classes, 'Notification');
  const [isOpen, setIsOpen] = useState(true);

  const handleOnClick = useCallback(() => {
    setIsOpen(!isOpen);
    toast.dismiss(toastId);

    handleClick?.();
  }, [handleClick, isOpen, toastId]);

  const icon = useMemo(() => {
    // If iconName is provided, use that
    if (iconName) return iconName;

    switch (kind) {
      case 'destructive':
      case 'error':
        return 'alert-circle';
      case 'success':
        return 'check';
      default:
        return null;
    }
  }, [iconName, kind]);

  const content = useMemo(() => {
    if (children) return children;

    return (
      <Flex gap="2px" layout="col">
        {!!title && <div className={bem('-title')}>{title}</div>}
        {!!description && <div className={bem('-description')}>{description}</div>}
      </Flex>
    );
  }, [bem, children, description, title]);

  return (
    <Box
      className={bem(
        '&',
        'rm-Notification',
        `_${isOpen ? 'open' : 'close'}`,
        theme && `_${theme}`,
        kind && `_${kind}`,
        className,
      )}
      data-color-mode={theme}
      kind="pop"
      style={style}
      {...ariaProps}
    >
      {!!icon && (
        <div className={bem('-icon')}>
          <Icon name={icon} size="md" />
        </div>
      )}

      {content}

      {!!dismissible && (
        <Button
          aria-label="Dismiss notification"
          className={bem('-dismissBtn')}
          ghost
          kind="minimum"
          onClick={handleOnClick}
          size="sm"
          text
        >
          <Icon name="x" />
        </Button>
      )}
    </Box>
  );
};

export { default as ErrorNotification } from './ErrorNotification';
export { default as NotificationToaster, BaseToaster as BaseNotificationToaster } from './Toaster';
export default Notification;
